<template>
  <div />
</template>

<script>
/**
 * Vue: Content card slider (content-card-slider)
 * -----------------------------------------------------------------------------
 * Content card slider.
 * - Limited interactivity section.
 *
 * @param {String} liquid.blockLength - Section's block length.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

import { values as breakpoints } from '~/config/breakpoints';

export default {
  name: 'ContentCardSlider',

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blockLength: 0,
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      activeCarousel: false,
      carousel: null,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
  },

  watch: {
    /**
     * Watch is mobile update to handle resize.
     */
    async isMobile() {
      await this.$nextTick();
      this.handleResize();
    },
  },

  mounted() {
    if (this.carousel) {
      return;
    }

    const selector = `#vue-element [js-content-card-slider="${this.liquid.sectionId}"]`;

    this.constructCarousel(selector);

    if (!this.carousel) {
      return;
    }

    if (
      (this.isMobile && this.liquid.blockLength > 1) ||
      (!this.isMobile && this.liquid.blockLength > 3)
    ) {
      this.carousel.mount();
      this.activeCarousel = true;
    }
  },

  methods: {
    /**
     * Construct and mount the carousel.
     * @param {HTMLElement} selector - Carousel selector.
     */
    constructCarousel(selector) {
      if (!selector) {
        return;
      }

      this.carousel = new Splide(selector, {
        arrows: false,
        focus: 0,
        gap: 'var(--layout-gutter)',
        mediaQuery: 'min',
        omitEnd: true,
        padding: {
          left: 'var(--layout-margin)',
          right: 'var(--layout-margin)',
        },
        pagination: true,
        perPage: 1,
        breakpoints: {
          [breakpoints.m]: {
            perPage: 2,
          },
          [breakpoints.l]: {
            arrows: true,
            drag: false,
            pagination: false,
            padding: 0,
            perPage: 3,
          },
        },
      });
    },

    /**
     * Handle resize event.
     */
    handleResize() {
      if (
        this.isMobile ||
        this.liquid.blockLength > 1 ||
        (!this.isMobile && this.liquid.blockLength > 3)
      ) {
        if (this.activeCarousel) {
          return;
        }

        this.carousel.mount();
        this.activeCarousel = true;
        return;
      }

      if (
        (this.isMobile && this.liquid.blockLength === 1) ||
        (!this.isMobile && this.liquid.blockLength <= 3)
      ) {
        this.carousel.destroy();
        this.activeCarousel = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import './content-card-slider';
</style>
